import CloseIcon from '@mui/icons-material/Close';
import UpdateIcon from '@mui/icons-material/Update';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import { ToggleButtonGroupDesignSystemScope } from '@openx/components-core/lib/ToggleButtonGroupField/ToggleButtonGroupDesignSystemScope';
import { MultiSelectFilterDropdown } from '@openx/components-filters/lib/FastFilters/MultiSelectFilterDropdown';
import { MoreFiltersButton } from '@openx/components-filters/lib/FiltersBar/components/moreFilters/MoreFiltersButton';
import type { FiltersConfig } from '@openx/types/chipFilters';
import type { DealsListFiltersValue } from '@openx/types/deal';
import { parseMultiValueFilter, serializeMultiValueFilter } from '@openx/utils/lib/multiValueFilter';
import { memo, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { clearFastFilterEntries } from './DealsFastFilters.utils';
import { FastFiltersMetricRange } from './parts/FastFiltersMetricRange';
import { StatusPill, type StatusPillTone } from './parts/StatusPill';

interface DealsFastFiltersBarProps {
  filters: DealsListFiltersValue;
  filtersConfig: FiltersConfig;
  onChange: (filters: DealsListFiltersValue) => void;
  onOpenMoreFilters: () => void;
  moreFiltersActiveCount?: number;
}

const CONTROL_HEIGHT = 40;

const getStatusTone = (statusValue: string): StatusPillTone | null => {
  switch (statusValue.toLowerCase()) {
    case 'active':
      return 'success';
    case 'publishing':
    case 'goal reached':
      return 'info';
    case 'paused':
      return 'beige';
    case 'expired':
      return 'neutral';
    case 'error':
      return 'pink';
    default:
      return null;
  }
};

const parseStatusValues = (status: DealsListFiltersValue['status']): string[] => {
  if (!status) return [];
  return parseMultiValueFilter(String(status));
};

export const DealsFastFiltersBar = memo(
  ({ filters, filtersConfig, onChange, onOpenMoreFilters, moreFiltersActiveCount = 0 }: DealsFastFiltersBarProps) => {
    const { t } = useTranslation();
    const myDealsFilterConfig = filtersConfig.myDeals;
    const myDealsValue = String(myDealsFilterConfig?.options?.[0]?.value ?? '');
    const currentCreatedByValues = parseMultiValueFilter(filters.createdBy);
    const isMyDealsSelected =
      !!myDealsValue && currentCreatedByValues.length === 1 && currentCreatedByValues[0] === myDealsValue;

    const statusConfig = filtersConfig.status;
    const healthScoreConfig = filtersConfig.health_score;
    const createdByConfig = filtersConfig.createdBy;
    const accountConfig = filtersConfig.account;

    const hasActiveFilters =
      Boolean(filters.status) ||
      Boolean(filters.createdBy) ||
      Boolean(filters.account) ||
      filters.health_score_min != null ||
      filters.health_score_max != null;

    const onDealsScopeChange = (_event: SyntheticEvent, value: string | null) => {
      if (!value) return;

      if (value === 'my') {
        onChange({ ...filters, createdBy: myDealsValue });
      } else {
        const { createdBy, ...rest } = filters;
        onChange(rest);
      }
    };

    return (
      <>
        {myDealsFilterConfig?.options && myDealsFilterConfig.options.length > 0 && (
          <ToggleButtonGroupDesignSystemScope>
            <ToggleButtonGroup
              exclusive
              value={isMyDealsSelected ? 'my' : 'all'}
              size="small"
              onChange={onDealsScopeChange}
              data-test="deals-fast-tabs"
              sx={{
                height: CONTROL_HEIGHT,
                flexShrink: 0,
                '& .MuiToggleButtonGroup-grouped': {
                  height: CONTROL_HEIGHT,
                  px: 2,
                  textTransform: 'uppercase',
                  backgroundColor: 'background.paper',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  color: 'text.primary',
                  fontWeight: 'fontWeightMedium',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    border: theme => `1px solid ${theme.palette.divider}`,
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                    color: 'primary.main',
                    border: theme => `1px solid ${theme.palette.primary.main}`,
                    fontWeight: 700,
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: theme =>
                      alpha(
                        theme.palette.primary.main,
                        theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
                      ),
                    border: theme => `1px solid ${theme.palette.primary.main}`,
                  },
                  '&.Mui-selected + .MuiToggleButtonGroup-grouped': {
                    borderLeftColor: theme => theme.palette.primary.main,
                  },
                },
              }}
            >
              <ToggleButton value="all" data-test="show-all-deals">
                {t('All Deals')}
              </ToggleButton>
              <ToggleButton value="my" data-test="show-my-deals">
                {t('My Deals')}
              </ToggleButton>
            </ToggleButtonGroup>
          </ToggleButtonGroupDesignSystemScope>
        )}

        {statusConfig && (
          <MultiSelectFilterDropdown
            label={statusConfig.name}
            options={(statusConfig.options ?? []).map(option => ({
              value: String(option.value),
              label: String(option.displayName ?? option.value),
            }))}
            selectedValues={parseStatusValues(filters.status)}
            controlHeight={CONTROL_HEIGHT}
            dataTest="deals-fast-status-multiselect"
            isActive={parseStatusValues(filters.status).length > 0}
            renderOption={option => {
              const tone = getStatusTone(option.label);
              if (!tone) return option.label;

              return (
                <StatusPill
                  label={option.label}
                  tone={tone}
                  endIcon={
                    option.label.toLowerCase() === 'publishing' ? <UpdateIcon sx={{ fontSize: 14 }} /> : undefined
                  }
                />
              );
            }}
            onApply={values => {
              onChange({
                ...filters,
                status: values.length ? serializeMultiValueFilter(values) : undefined,
              });
            }}
          />
        )}

        {healthScoreConfig?.numberFilter && (
          <FastFiltersMetricRange
            filterConfig={healthScoreConfig}
            minValue={filters.health_score_min}
            maxValue={filters.health_score_max}
            onApply={(minValue, maxValue) =>
              onChange({
                ...filters,
                health_score_min: minValue,
                health_score_max: maxValue,
              })
            }
            controlHeight={CONTROL_HEIGHT}
            dataTest="deals-fast-health-score-range"
          />
        )}

        {createdByConfig && (
          <MultiSelectFilterDropdown
            label={createdByConfig.name}
            options={(createdByConfig.options ?? []).map(option => ({
              value: String(option.value),
              label: String(option.displayName ?? option.value),
            }))}
            selectedValues={parseMultiValueFilter(filters.createdBy)}
            showSearch
            searchPlaceholder={t('Search user')}
            controlHeight={CONTROL_HEIGHT}
            dataTest="deals-fast-createdBy-multiselect"
            renderOption={(option, isChecked) => (
              <Box component="span" sx={{ display: 'block', width: '100%', fontWeight: isChecked ? 600 : 400 }}>
                <Ellipsis width="100%" tooltip detectTooltipResize tooltipPlacement="top-start">
                  {option.label}
                </Ellipsis>
              </Box>
            )}
            isActive={parseMultiValueFilter(filters.createdBy).length > 0}
            onApply={values => {
              onChange({
                ...filters,
                createdBy: values.length ? serializeMultiValueFilter(values) : undefined,
              });
            }}
          />
        )}

        {accountConfig && (
          <MultiSelectFilterDropdown
            label={accountConfig.name}
            loading={accountConfig.loading}
            options={(accountConfig.options ?? []).map(option => ({
              value: String(option.value),
              label: String(option.displayName ?? option.value),
            }))}
            selectedValues={parseMultiValueFilter(filters.account)}
            showSearch
            searchPlaceholder={t('Search Organization')}
            controlHeight={CONTROL_HEIGHT}
            dataTest="deals-fast-account-multiselect"
            renderOption={(option, isChecked) => (
              <Box component="span" sx={{ display: 'block', width: '100%', fontWeight: isChecked ? 600 : 400 }}>
                <Ellipsis width="100%" tooltip detectTooltipResize tooltipPlacement="top-start">
                  {option.label}
                </Ellipsis>
              </Box>
            )}
            isActive={parseMultiValueFilter(filters.account).length > 0}
            onApply={values => {
              onChange({
                ...filters,
                account: values.length ? serializeMultiValueFilter(values) : undefined,
              });
            }}
          />
        )}

        <MoreFiltersButton onClick={onOpenMoreFilters} activeFiltersCount={moreFiltersActiveCount} />

        {hasActiveFilters && (
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={() => onChange(clearFastFilterEntries(filters))}
            data-test="deals-fast-clear-filters-button"
            sx={{ height: CONTROL_HEIGHT, flexShrink: 0 }}
          >
            {t('Clear Filters')}
          </Button>
        )}
      </>
    );
  }
);
